import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Brand } from 'apis/enterprise/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import Loading from 'core/Loading';
import PageLoadError from 'core/PageLoadError';
import { ExperienceModuleContextProps, ExperienceModuleContextProvider } from 'modules/experience-core';
import { ExperienceEntityTree, ExperienceEntity, ExperienceEntityType } from 'modules/experience-core';
import { recursiveGetOfType } from 'modules/experience-core';
import { getInitials } from 'modules/experience-core';

import { SpinnerContainer } from '../Experience.styled';
import { useActiveNavigation } from '../hooks/useActiveNavigation';
import { useBrand } from '../hooks/useBrand';
import { useSidebar } from '../hooks/useSidebar';
import { IndexPage } from '../pages/IndexPage';

const ContextAwareBrandExperience = ({
  experienceEntity,
  experienceEntityTree,
}: {
  experienceEntity: ExperienceEntity;
  experienceEntityTree: ExperienceEntityTree;
}) => {
  // Naming
  const brand = experienceEntity.experienceEntityObject as Brand;
  const title = brand.name;

  // Navigation
  const { activeNavigation, setActiveNavigation } = useActiveNavigation();
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const [selectedExperienceEntity, setSelectedExperienceEntity] = useState<ExperienceEntity>(experienceEntity);

  // Venue
  const venueExperienceEntities = recursiveGetOfType(experienceEntityTree, ExperienceEntityType.VENUE).filter(
    venueExperienceEntity => (venueExperienceEntity.experienceEntityObject as Venue).status === 'VENUE_PUBLISHED',
  );
  const [firstVenueExperienceEntity] = venueExperienceEntities;
  const firstVenue = venueExperienceEntities[0].experienceEntityObject as Venue;
  const [selectedVenueExperienceEntity, setSelectedVenueExperienceEntity] =
    useState<ExperienceEntity>(firstVenueExperienceEntity);

  const contextValue: ExperienceModuleContextProps = useMemo(
    () => ({
      experienceEntity,
      experienceEntityTree,
      country: (brand.country as Country) || 'DEU', // check why brand.country is optional and not Country type
      currency: firstVenue?.currency,
      timezone: firstVenue?.timezone,
      parentEntityId: brand.enterpriseId,
      name: brand.name,
      address: (brand.country as Country).toString() || '',
      city: '',
      image: '',
      initials: getInitials(brand.name),
      isWoltPlus: false,
      activeNavigation,
      setActiveNavigation,
      isSidebarOpen,
      setIsSidebarOpen,
      selectedExperienceEntity,
      setSelectedExperienceEntity,
      selectedVenueExperienceEntity,
      setSelectedVenueExperienceEntity,
    }),
    [
      brand,
      firstVenue,
      activeNavigation,
      isSidebarOpen,
      selectedExperienceEntity,
      selectedVenueExperienceEntity,
      setActiveNavigation,
      setIsSidebarOpen,
      experienceEntity,
      experienceEntityTree,
      setSelectedExperienceEntity,
      setSelectedVenueExperienceEntity,
    ],
  );

  return (
    <ExperienceModuleContextProvider value={contextValue}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
      </Helmet>
      <IndexPage />
    </ExperienceModuleContextProvider>
  );
};

export const BrandExperience = ({ experienceEntityId }: { experienceEntityId: string }) => {
  const { experienceEntity, experienceEntityTree, error, isLoading } = useBrand(experienceEntityId);

  return (
    <>
      {isLoading && (
        <SpinnerContainer>
          <Loading />
        </SpinnerContainer>
      )}
      {!isLoading && experienceEntity && experienceEntityTree && (
        <ContextAwareBrandExperience experienceEntity={experienceEntity} experienceEntityTree={experienceEntityTree} />
      )}
      {!isLoading && error && <PageLoadError />}
    </>
  );
};
